import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { message } from 'ant-design-vue'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/views/Layout.vue'),
    redirect: () => {
      const firstAccessibleRoute = findFirstAccessibleRoute()
      return firstAccessibleRoute || '/profile'
    },
    meta: { requiresAuth: true },
    children: [
      {
        path: 'customers',
        name: 'Customers',
        children: [
          {
            path: 'all',
            name: 'AllCustomers',
            component: () => import('@/views/customers/AllCustomers.vue'),
            meta: { title: '全部客户', menuPermission: 'menu.customer.all' }
          },
          {
            path: 'my',
            name: 'MyCustomers',
            component: () => import('@/views/customers/MyCustomers.vue'),
            meta: { title: '我的客户', menuPermission: 'menu.customer.my' }
          },
          {
            path: ':id',
            name: 'CustomerDetail',
            component: () => import('@/views/customers/CustomerDetail.vue'),
            meta: { title: '客户详情', menuPermission: ['menu.customer.all', 'menu.customer.my'] }
          }
        ]
      },
      {
        path: 'admin',
        name: 'Admin',
        children: [
          {
            path: 'login-logs',
            name: 'LoginLogManagement',
            component: () => import('@/views/admin/LoginLogManagement.vue'),
            meta: { title: '登录日志', menuPermission: 'menu.admin.login_logs' }
          },
          {
            path: 'system',
            name: 'SystemConfig',
            component: () => import('@/views/admin/SystemConfig.vue'),
            meta: { title: '系统配置', menuPermission: 'menu.admin.system' }
          },
          {
            path: 'import',
            name: 'ExcelImport',
            component: () => import('@/views/admin/ExcelImport.vue'),
            meta: { title: '数据管理', menuPermission: 'menu.admin.import' }
          },
          {
            path: 'permissions',
            name: 'PermissionManagement',
            component: () => import('@/views/admin/PermissionManagement.vue'),
            meta: { title: '权限管理', menuPermission: 'menu.admin.permission' }
          }
        ]
      },
      {
        path: 'manager',
        name: 'Manager',
        children: [
          {
            path: 'team-performance',
            name: 'TeamPerformance',
            component: () => import('@/views/performance/ManagerReport.vue'),
            meta: { title: '团队业绩', menuPermission: 'menu.manager.team' }
          }
        ]
      },
      {
        path: 'performance',
        name: 'Performance',
        children: [
          {
            path: 'admin',
            name: 'AdminPerformance',
            component: () => import('@/views/performance/AdminDashboard.vue'),
            meta: { title: '业绩总览', menuPermission: 'func.performance.admin' }
          },
          {
            path: 'mine',
            name: 'MyPerformance',
            component: () => import('@/views/performance/SalespersonDashboard.vue'),
            meta: { title: '工作看板', menuPermission: 'menu.performance' }
          }
        ]
      },
      {
        path: 'work-orders',
        name: 'WorkOrders',
        children: [
          {
            path: '',
            name: 'WorkOrderList',
            component: () => import('@/views/work-orders/WorkOrderList.vue'),
            meta: { title: '工单列表', menuPermission: 'menu.work_order.list' }
          },
          {
            path: 'my',
            name: 'MyWorkOrders',
            component: () => import('@/views/work-orders/MyWorkOrders.vue'),
            meta: { title: '我的工单', menuPermission: 'menu.work_order.my' }
          },
          {
            path: 'kanban',
            name: 'WorkOrderKanban',
            component: () => import('@/views/work-orders/WorkOrderKanban.vue'),
            meta: { title: '工单看板', menuPermission: 'menu.work_order.kanban' }
          }
        ]
      },
      {
        path: 'knowledge',
        name: 'Knowledge',
        children: [
          {
            path: '',
            name: 'KnowledgeIndex',
            component: () => import('@/views/knowledge/Index.vue'),
            meta: { title: '知识库首页', menuPermission: 'menu.knowledge.index' }
          },
          {
            path: 'articles',
            name: 'KnowledgeArticleList',
            component: () => import('@/views/knowledge/ArticleList.vue'),
            meta: { title: '文章列表', menuPermission: 'menu.knowledge.articles' }
          },
          {
            path: 'article/create',
            name: 'KnowledgeArticleCreate',
            component: () => import('@/views/knowledge/ArticleEdit.vue'),
            meta: { title: '创建文章', menuPermission: 'menu.knowledge.create' }
          },
          {
            path: 'article/edit/:mode/:id?',
            name: 'KnowledgeArticleEdit',
            component: () => import('@/views/knowledge/ArticleEdit.vue'),
            meta: { title: '编辑文章', funcPermission: 'func.knowledge.edit' }
          },
          {
            path: 'article/:id',
            name: 'KnowledgeDetail',
            component: () => import('@/views/knowledge/ArticleDetail.vue'),
            meta: { title: '文章详情', menuPermission: 'menu.knowledge.articles' }
          },
          {
            path: 'categories',
            name: 'KnowledgeCategoryManage',
            component: () => import('@/views/knowledge/CategoryManage.vue'),
            meta: { title: '分类管理', menuPermission: 'menu.knowledge.categories' }
          }
        ]
      },
      // OA模块 - 组织架构
      {
        path: 'organization',
        name: 'Organization',
        children: [
          {
            path: 'departments',
            name: 'DepartmentManagement',
            component: () => import('@/views/organization/DepartmentView.vue'),
            meta: { title: '部门管理', menuPermission: 'menu.organization.departments' }
          },
          {
            path: 'positions',
            name: 'PositionManagement',
            component: () => import('@/views/organization/PositionView.vue'),
            meta: { title: '职位管理', menuPermission: 'menu.organization.positions' }
          },
          {
            path: 'employees',
            name: 'EmployeeManagement',
            component: () => import('@/views/organization/EmployeeView.vue'),
            meta: { title: '员工管理', menuPermission: 'menu.organization.employees' }
          },
          {
            path: 'roles',
            name: 'RoleManagement',
            component: () => import('@/views/organization/RoleView.vue'),
            meta: { title: '角色管理', menuPermission: 'menu.organization.roles' }
          }
        ]
      },
      // OA模块 - 考勤管理
      {
        path: 'attendance',
        name: 'Attendance',
        children: [
          {
            path: 'checkin',
            name: 'AttendanceCheckIn',
            component: () => import('@/views/attendance/AttendanceCheckIn.vue'),
            meta: { title: '考勤打卡', menuPermission: 'menu.attendance.checkin' }
          },
          {
            path: 'statistics',
            name: 'AttendanceStatistics',
            component: () => import('@/views/attendance/AttendanceStatistics.vue'),
            meta: { title: '考勤统计', menuPermission: 'menu.attendance.statistics' }
          },
          {
            path: 'setting',
            name: 'AttendanceSetting',
            component: () => import('@/views/attendance/AttendanceSetting.vue'),
            meta: { title: '考勤设置', menuPermission: 'menu.attendance.setting' }
          },
          {
            path: 'management',
            name: 'AttendanceManagement',
            component: () => import('@/views/attendance/AttendanceManagement.vue'),
            meta: { title: '员工考勤管理', menuPermission: 'menu.attendance.management' }
          }
        ]
      },
      // OA模块 - 审批管理
      {
        path: 'approval',
        name: 'Approval',
        children: [
          {
            path: '',
            name: 'ApprovalList',
            component: () => import('@/views/approval/ApprovalList.vue'),
            meta: { title: '审批中心', menuPermission: 'menu.approval.list' }
          },
          {
            path: 'templates',
            name: 'ApprovalTemplateManage',
            component: () => import('@/views/approval/ApprovalTemplateManage.vue'),
            meta: { title: '审批模板管理', menuPermission: 'menu.approval.templates' }
          },
          {
            path: 'templates/:id',
            name: 'ApprovalTemplateDetail',
            component: () => import('@/views/approval/ApprovalTemplateDetail.vue'),
            meta: { title: '审批模板详情', menuPermission: 'menu.approval.templates' }
          },
          {
            path: 'templates/:id/process',
            name: 'ApprovalProcessManage',
            component: () => import('@/views/approval/ApprovalProcessManage.vue'),
            meta: { title: '审批流程配置', menuPermission: 'menu.approval.templates' }
          },
          {
            path: 'instances/:id',
            name: 'ApprovalDetail',
            component: () => import('@/views/approval/ApprovalDetail.vue'),
            meta: { title: '审批详情', menuPermission: 'menu.approval.list' }
          }
        ]
      },
      // OA模块 - 文档管理
      {
        path: 'document',
        name: 'Document',
        children: [
          {
            path: '',
            name: 'DocumentManager',
            component: () => import('@/views/document/DocumentManager.vue'),
            meta: { title: '文档管理', menuPermission: 'menu.document.center' }
          }
        ]
      },
      // OA模块 - 合同管理
      {
        path: 'contracts',
        name: 'Contracts',
        children: [
          {
            path: '',
            name: 'ContractList',
            component: () => import('@/views/contracts/ContractList.vue'),
            meta: { title: '合同管理', menuPermission: 'menu.contract.list' }
          },
          {
            path: 'templates',
            name: 'ContractTemplates',
            component: () => import('@/views/contracts/ContractTemplateList.vue'),
            meta: { title: '合同模板', menuPermission: 'menu.contract.templates' }
          },
          {
            path: 'tags',
            name: 'ContractTags',
            component: () => import('@/views/contracts/ContractTagList.vue'),
            meta: { title: '合同标签', menuPermission: 'menu.contract.tags' }
          },
          {
            path: ':id',
            name: 'ContractDetail',
            component: () => import('@/views/contracts/ContractDetail.vue'),
            meta: { title: '合同详情', funcPermission: 'func.contract.view' }
          },
          {
            path: ':id/edit',
            name: 'ContractForm',
            component: () => import('@/views/contracts/ContractForm.vue'),
            meta: { title: '合同编辑', funcPermission: 'func.contract.edit' }
          },
          {
            path: ':id/change',
            name: 'ContractChange',
            component: () => import('@/views/contracts/ContractChange.vue'),
            meta: { title: '合同变更', funcPermission: 'func.contract.change' }
          },
          {
            path: 'create',
            name: 'ContractCreate',
            component: () => import('@/views/contracts/ContractForm.vue'),
            meta: { title: '新建合同', funcPermission: 'func.contract.create' }
          }
        ]
      },
      // OA模块 - 公告通知
      {
        path: 'notice',
        name: 'Notice',
        children: [
          {
            path: '',
            name: 'NoticeList',
            component: () => import('@/views/notice/NoticeList.vue'),
            meta: { title: '公告通知', menuPermission: 'menu.notice.list' }
          }
        ]
      },
      // 个人中心
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: '个人中心', menuPermission: 'menu.profile' }
      },
      // 研发管理
      {
        path: 'research',
        name: 'Research',
        children: [
          {
            path: '',
            name: 'ResearchHome',
            component: () => import('@/views/research/Home.vue'),
            meta: { title: '研究首页', menuPermission: 'menu.research.home' }
          },
          {
            path: 'content-library',
            name: 'ContentLibrary',
            component: () => import('@/views/research/ContentLibrary.vue'),
            meta: { title: '内容库', menuPermission: 'menu.research.content' }
          },
          {
            path: 'category/:id',
            name: 'CategoryContent',
            component: () => import('@/views/research/CategoryContent.vue'),
            meta: { title: '分类内容', menuPermission: 'menu.research.content' }
          },
          {
            path: 'content/create/:categoryId?',
            name: 'ContentCreate',
            component: () => import('@/views/research/ContentForm.vue'),
            meta: { title: '创建内容', menuPermission: 'menu.research.content' }
          },
          {
            path: 'content/:id',
            name: 'ContentDetail',
            component: () => import('@/views/research/ContentDetail.vue'),
            meta: { title: '内容详情', menuPermission: 'menu.research.content' }
          },
          {
            path: 'content/:id/edit',
            name: 'ContentEdit',
            component: () => import('@/views/research/ContentForm.vue'),
            meta: { title: '编辑内容', menuPermission: 'menu.research.content' }
          },
          {
            path: 'content/:id/edit-history',
            name: 'ContentEditHistory',
            component: () => import('@/views/research/ContentEditHistory.vue'),
            meta: { title: '编辑历史', menuPermission: 'menu.research.content' }
          },
          {
            path: 'documents',
            name: 'DocumentLibrary',
            component: () => import('@/views/research/DocumentLibrary.vue'),
            meta: { title: '文档库', menuPermission: 'menu.research.documents' }
          },
          {
            path: 'document/:id',
            name: 'DocumentViewer',
            component: () => import('@/views/research/DocumentViewer.vue'),
            meta: { title: '文档查看器', menuPermission: 'menu.research.documents' }
          },
          {
            path: 'document/:id/favorite-mode',
            name: 'DocumentFavoriteViewer',
            component: () => import('@/views/research/DocumentFavoriteViewer.vue'),
            meta: { title: '预览&收藏模式', menuPermission: 'menu.research.favorites' }
          },
          {
            path: 'document/upload',
            name: 'DocumentUpload',
            component: () => import('@/views/research/DocumentUpload.vue'),
            meta: { title: '上传文档', menuPermission: 'menu.research.documents' }
          },
          {
            path: 'document/:id/edit',
            name: 'DocumentEdit',
            component: () => import('@/views/research/DocumentUpload.vue'),
            meta: { title: '编辑文档', menuPermission: 'menu.research.documents' }
          },
          {
            path: 'categories',
            name: 'CategoryManagement',
            component: () => import('@/views/research/CategoryManagement.vue'),
            meta: { title: '分类管理', menuPermission: 'menu.research.categories' }
          },
          {
            path: 'tags',
            name: 'TagManagement',
            component: () => import('@/views/research/TagManagement.vue'),
            meta: { title: '标签管理', menuPermission: 'menu.research.tags' }
          },
          {
            path: 'permissions',
            name: 'ResearchPermissionManagement',
            component: () => import('@/views/research/PermissionManagement.vue'),
            meta: { title: '研发权限管理', menuPermission: 'menu.research.permissions' }
          },
          {
            path: 'favorites',
            name: 'DocumentFavorites',
            component: () => import('@/views/research/DocumentFavorites.vue'),
            meta: { title: '文档收藏夹', menuPermission: 'menu.research.favorites' }
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const token = store.state.auth.token
  const userRole = store.getters['auth/userRole']
  const isAdmin = store.getters['auth/isAdmin']

  if (to.meta.requiresAuth && !token) {
    next('/login')
  } else if (to.path === '/login' && token) {
    const firstAccessibleRoute = findFirstAccessibleRoute()
    if (firstAccessibleRoute) {
      next(firstAccessibleRoute)
    } else {
      message.error('您没有任何访问权限，请联系管理员')
      next('/login')
    }
  } else if (to.meta.requiresAdmin && !isAdmin) {
    next('/')
  } else if (to.meta.requiresManager && !isAdmin && userRole !== 'sales_manager') {
    next('/')
  } else if (to.meta.menuPermission) {
    // 检查菜单权限（只有已登录用户才检查权限）
    if (!token) {
      next('/login')
      return
    }
    // 支持数组形式的权限（任一权限满足即可）
    const requiredPermissions = Array.isArray(to.meta.menuPermission) 
      ? to.meta.menuPermission 
      : [to.meta.menuPermission]
    const hasMenuPermission = requiredPermissions.some(perm => 
      store.getters['auth/hasMenuPermission'](perm)
    )
    if (!hasMenuPermission) {
      message.error('您没有权限访问该页面')
      // 如果有来源页面则返回，否则跳转到用户有权限的第一个页面
      if (from.path && from.path !== '/') {
        next(false)
      } else {
        const firstAccessibleRoute = findFirstAccessibleRoute()
        if (firstAccessibleRoute) {
          next(firstAccessibleRoute)
        } else {
          message.error('您没有任何访问权限，请联系管理员')
          next('/login')
        }
      }
    } else {
      next()
    }
  } else if (to.meta.funcPermission) {
    // 检查功能权限（只有已登录用户才检查权限）
    if (!token) {
      next('/login')
      return
    }
    const hasFuncPermission = store.getters['auth/hasPermission'](to.meta.funcPermission)
    if (!hasFuncPermission) {
      message.error('您没有权限访问该页面')
      // 如果有来源页面则返回，否则跳转到用户有权限的第一个页面
      if (from.path && from.path !== '/') {
        next(false)
      } else {
        const firstAccessibleRoute = findFirstAccessibleRoute()
        if (firstAccessibleRoute) {
          next(firstAccessibleRoute)
        } else {
          message.error('您没有任何访问权限，请联系管理员')
          next('/login')
        }
      }
    } else {
      next()
    }
  } else {
    next()
  }
})

// 查找用户有权限的第一个路由
function findFirstAccessibleRoute() {
  const layoutRoute = routes.find(r => r.path === '/')
  if (!layoutRoute || !layoutRoute.children) return null

  const isAdmin = store.getters['auth/isAdmin']

  for (const child of layoutRoute.children) {
    // 检查是否有子路由
    if (child.children) {
      for (const subChild of child.children) {
        // 超管可以访问所有有菜单权限的路由
        if (subChild.meta.menuPermission) {
          if (isAdmin || store.getters['auth/hasMenuPermission'](subChild.meta.menuPermission)) {
            return '/' + child.path + '/' + subChild.path
          }
        }
      }
    } else if (child.meta.menuPermission) {
      // 超管可以访问所有有菜单权限的路由
      if (isAdmin || store.getters['auth/hasMenuPermission'](child.meta.menuPermission)) {
        return '/' + child.path
      }
    }
  }
  return null
}

export default router
